import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { Modal, Field } from "../../components/ui-modal";
import { RichTextEditor } from "../../components/RichTextEditor";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";

const stripHtml = (h) => (h || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

export default function AdminHelp() {
  const { confirm, ask } = useConfirm();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("Toutes");
  const [editing, setEditing] = useState(null); // "new" | id
  const [form, setForm] = useState({ question: "", answer: "", category: "", published: true });
  const [dragI, setDragI] = useState(null);
  const [catModal, setCatModal] = useState(false);
  const [newCat, setNewCat] = useState("");

  const load = () => api.get("/admin/faqs").then((r) => setItems(r.data));
  const loadCats = () => api.get("/admin/faq-categories").then((r) => setCats(r.data));
  useEffect(() => { load(); loadCats(); }, []);

  const catNames = useMemo(() => cats.map((c) => c.name), [cats]);
  const openNew = () => { setForm({ question: "", answer: "", category: catNames[0] || "Général", published: true }); setEditing("new"); };
  const openEdit = (f) => { setForm({ question: f.question, answer: f.answer, category: f.category || (catNames[0] || "Général"), published: f.published }); setEditing(f.id); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.question.trim() || !stripHtml(form.answer)) return toast.error("Question et réponse requises");
    try {
      if (editing === "new") await api.post("/admin/faqs", form);
      else await api.put(`/admin/faqs/${editing}`, form);
      toast.success("FAQ enregistrée"); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const del = async (f) => { if (!(await confirm({ title: `Supprimer « ${f.question} » ?`, confirmLabel: "Supprimer", tone: "danger" }))) return; await api.delete(`/admin/faqs/${f.id}`); toast.success("FAQ supprimée"); load(); };

  const visible = filter === "Toutes" ? items : items.filter((f) => (f.category || "") === filter);
  const onDrop = async (targetId) => {
    if (dragI === null || targetId === dragI) { setDragI(null); return; }
    const dragged = items.find((x) => x.id === dragI);
    if (!dragged) { setDragI(null); return; }
    const a = items.filter((x) => x.id !== dragI);
    const ti = a.findIndex((x) => x.id === targetId);
    a.splice(ti, 0, dragged); setItems(a); setDragI(null);
    try { await api.put("/admin/faqs/reorder", { ids: a.map((x) => x.id) }); } catch { toast.error("Échec du réordonnancement"); }
  };

  // ---- Categories management ----
  const addCat = async () => {
    if (!newCat.trim()) return;
    try { await api.post("/admin/faq-categories", { name: newCat.trim() }); setNewCat(""); loadCats(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const renameCat = async (c) => {
    const name = await ask({ title: "Renommer la catégorie", label: "Nom de la catégorie", defaultValue: c.name, confirmLabel: "Renommer" });
    if (!name || name.trim() === c.name) return;
    try { await api.put(`/admin/faq-categories/${c.id}`, { name: name.trim() }); loadCats(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const setCatDesc = (id, v) => setCats(cats.map((c) => (c.id === id ? { ...c, description: v } : c)));
  const saveCatDesc = async (c) => { try { await api.put(`/admin/faq-categories/${c.id}`, { description: c.description || "" }); } catch { toast.error("Erreur d'enregistrement"); } };
  const delCat = async (c) => {
    if (!(await confirm({ title: `Supprimer la catégorie « ${c.name} » ?`, message: "Les FAQ associées seront déclassées.", confirmLabel: "Supprimer", tone: "danger" }))) return;
    await api.delete(`/admin/faq-categories/${c.id}`); loadCats(); load();
    if (filter === c.name) setFilter("Toutes");
  };
  const [catDragI, setCatDragI] = useState(null);
  const onCatDrop = async (targetId) => {
    if (catDragI === null || catDragI === targetId) return;
    const a = [...cats]; const di = a.findIndex((x) => x.id === catDragI); const [it] = a.splice(di, 1);
    const ti = a.findIndex((x) => x.id === targetId); a.splice(ti, 0, it); setCats(a); setCatDragI(null);
    await api.put("/admin/faq-categories/reorder", { ids: a.map((x) => x.id) });
  };

  const renderCard = (f, i) => (
    <div key={f.id} draggable onDragStart={() => setDragI(f.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(f.id)}
      data-testid={`faq-admin-${f.id}`} className="bg-sable rounded-sm border border-marine/10 p-4 flex items-start gap-3 hover:border-marine/30 transition-colors">
      <Icons.GripVertical size={16} className="text-muet mt-1 shrink-0 cursor-grab active:cursor-grabbing" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[9px] uppercase tracking-wider bg-marine-50 text-marine border border-marine/30 px-2 py-0.5 rounded-sm">{f.category || "Sans catégorie"}</span>
          {!f.published && <span className="text-[9px] uppercase tracking-wider bg-sable text-gris-texte border border-marine/30 px-2 py-0.5 rounded-sm">Brouillon</span>}
        </div>
        <div className="font-medium text-marine">{f.question}</div>
        <div className="text-sm text-marine/80 mt-1 max-h-10 overflow-hidden">{stripHtml(f.answer)}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={() => openEdit(f)} data-testid={`edit-faq-${f.id}`} className="text-marine hover:text-bordeaux" title="Modifier"><Icons.Pencil size={16} /></button>
        <button onClick={() => del(f)} data-testid={`delete-faq-${f.id}`} className="text-rouge/70 hover:text-rouge" title="Supprimer"><Icons.Trash2 size={16} /></button>
      </div>
    </div>
  );

  const grouped = filter === "Toutes";
  const groups = useMemo(() => {
    const order = ["Sans catégorie", ...catNames];
    const g = {};
    visible.forEach((f) => { const c = f.category || "Sans catégorie"; (g[c] = g[c] || []).push(f); });
    return Object.entries(g).sort((a, b) => (order.indexOf(a[0]) === -1 ? 99 : order.indexOf(a[0])) - (order.indexOf(b[0]) === -1 ? 99 : order.indexOf(b[0])));
  }, [visible, catNames]);

  return (
    <div>
      <PageTitle overline="Centre d'aide" title="Aide" action={
        <div className="flex items-center gap-2">
          <button onClick={() => setCatModal(true)} data-testid="manage-cats-btn" className="border border-marine/30 text-marine rounded-sm px-4 py-2.5 text-sm hover:bg-marine hover:text-white transition-colors flex items-center gap-2"><Icons.Tags size={16} /> Catégories</button>
          <button onClick={openNew} data-testid="new-faq-btn" className="bg-marine text-white rounded-sm px-5 py-2.5 text-sm hover:bg-marine-hover flex items-center gap-2"><Icons.Plus size={16} /> Nouvelle question</button>
        </div>
      } />
      <p className="text-sm text-marine/80 mb-4">Gérez les questions fréquentes visibles par vos clients. Glissez-déposez pour réordonner.</p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="faq-cat-tabs">
        {["Toutes", ...catNames].map((c) => (
          <button key={c} onClick={() => setFilter(c)} data-testid={`faq-tab-${c}`}
            className={`px-3 py-1.5 rounded-sm text-xs border transition-colors ${filter === c ? "bg-marine text-white border-marine" : "border-marine/20 text-marine hover:bg-white"}`}>{c}</button>
        ))}
      </div>

      {items.length === 0 && <p className="text-sm text-marine/80">Aucune question. Ajoutez-en une.</p>}
      {grouped ? (
        <div className="space-y-6" data-testid="faq-admin-list">
          {groups.map(([cat, list]) => (
            <div key={cat} data-testid={`faq-admin-group-${cat}`}>
              <h2 className="font-display text-lg text-marine mb-2">{cat} <span className="text-xs text-marine/80">· {list.length}</span></h2>
              <div className="grid gap-3">{list.map((f, i) => renderCard(f, i))}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-3" data-testid="faq-admin-list">{visible.map((f, i) => renderCard(f, i))}{visible.length === 0 && <p className="text-sm text-marine/80">Aucune question dans cette catégorie.</p>}</div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing === "new" ? "Nouvelle question" : "Modifier la question"} wide>
          <form onSubmit={save} className="space-y-3">
            <Field label="Question"><input required value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-question" className="inp" placeholder="Ex. Comment valider une étape de mon projet ?" /></Field>
            <Field label="Catégorie">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="faq-category" className="inp">
                {catNames.length === 0 && <option value="Général">Général</option>}
                {catNames.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Réponse"><RichTextEditor value={form.answer} onChange={(v) => setForm({ ...form, answer: v })} testid="faq-answer-input" /></Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="faq-published" className="accent-marine" /> Publié (visible par les clients)</label>
            <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-marine/80">Annuler</button><button data-testid="faq-submit" className="bg-marine text-white rounded-sm px-5 py-2 text-sm">Enregistrer</button></div>
          </form>
        </Modal>
      )}

      {catModal && (
        <Modal onClose={() => setCatModal(false)} title="Gérer les catégories">
          <div data-testid="cat-manager">
            <div className="flex gap-2 mb-4">
              <input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCat(); } }} data-testid="new-cat-input" placeholder="Nouvelle catégorie…" className="flex-1 border border-marine/20 rounded-sm p-2 text-sm" />
              <button type="button" onClick={addCat} data-testid="add-cat-btn" className="bg-bordeaux text-white rounded-sm px-3 text-sm">Ajouter</button>
            </div>
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {cats.map((c) => (
                <div key={c.id} draggable onDragStart={() => setCatDragI(c.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onCatDrop(c.id)}
                  data-testid={`cat-row-${c.id}`} className="border border-marine/15 rounded-sm p-2 bg-creme">
                  <div className="flex items-center gap-2">
                    <Icons.GripVertical size={16} className="text-muet shrink-0 cursor-grab active:cursor-grabbing" />
                    <span className="text-sm text-marine flex-1">{c.name}</span>
                    <button type="button" onClick={() => renameCat(c)} data-testid={`rename-cat-${c.id}`} className="text-marine hover:text-bordeaux" title="Renommer"><Icons.Pencil size={16} /></button>
                    <button type="button" onClick={() => delCat(c)} data-testid={`del-cat-${c.id}`} className="text-rouge/70 hover:text-rouge" title="Supprimer"><Icons.Trash2 size={16} /></button>
                  </div>
                  <textarea value={c.description || ""} onChange={(e) => setCatDesc(c.id, e.target.value)} onBlur={() => saveCatDesc(c)} data-testid={`cat-desc-${c.id}`} rows={2}
                    placeholder="Description de la catégorie (visible sur le portail client)…"
                    className="w-full mt-2 border border-marine/15 rounded-sm p-2 text-xs text-marine outline-none focus:ring-1 focus:ring-marine" />
                </div>
              ))}
              {cats.length === 0 && <p className="text-sm text-marine/80">Aucune catégorie.</p>}
            </div>
            <div className="flex justify-end mt-4"><button onClick={() => setCatModal(false)} className="bg-marine text-white rounded-sm px-5 py-2 text-sm">Fermer</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
